import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Users, FileText, MessageSquare, Image, Flag, ShieldCheck, Ban, BarChart3,
  ClipboardList, Search, ChevronLeft, ChevronRight, AlertTriangle, Eye, EyeOff,
  Trash2, RotateCcw, UserCheck, Shield, Lock, Send, Mail, Settings2, ToggleLeft, ToggleRight,
  Award, GraduationCap, FileCheck, Download, Crown, Plus, X, Megaphone, ExternalLink,
  TrendingUp, MousePointerClick, Pause, Play, Link2, MapPin, Target
} from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = ["super_admin", "admin", "moderator", "verification_admin"];

const sidebarItems = [
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "posts", label: "Posts", icon: FileText },
  { id: "reports", label: "Reports", icon: Flag },
  { id: "verifications", label: "Verifications", icon: ShieldCheck },
  { id: "certifications", label: "Certifications", icon: Award },
  { id: "premium", label: "Premium", icon: Crown },
  { id: "ads", label: "Ads", icon: Megaphone },
  { id: "parental", label: "Parental Links", icon: Shield },
  { id: "banned", label: "Banned/Suspended", icon: Ban },
  { id: "media", label: "Media", icon: Image },
  { id: "audit", label: "Audit Log", icon: ClipboardList },
  { id: "email", label: "Email Config", icon: Send },
  { id: "settings", label: "Platform Settings", icon: Settings2 },
  { id: "cleanup", label: "Test Cleanup", icon: Trash2 },
];

/* ---- Confirmation Modal ---- */
function ConfirmModal({ open, onClose, title, description, onConfirm, loading, destructive, requireReason, reasonValue, onReasonChange }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            {destructive && <AlertTriangle size={18} className="text-[#F85037]" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-500">{description}</p>
        {requireReason && (
          <div className="mt-2">
            <Label className="text-xs text-zinc-600">Reason (required)</Label>
            <Textarea value={reasonValue} onChange={(e) => onReasonChange(e.target.value)} placeholder="Provide a reason..." className="mt-1 border-[#E6E6E6] min-h-[60px]" data-testid="confirm-reason" />
          </div>
        )}
        <div className="flex gap-2 mt-4 justify-end">
          <Button variant="outline" onClick={() => onClose(false)} className="border-[#E6E6E6]" data-testid="confirm-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading || (requireReason && !reasonValue?.trim())} className={destructive ? "bg-[#F85037] hover:bg-[#e04530] text-white" : "bg-[#074572] hover:bg-[#053a60] text-white"} data-testid="confirm-action">
            {loading ? "Processing..." : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---- Analytics Section ---- */
function AnalyticsSection({ data }) {
  if (!data) return <div className="text-center py-12 text-zinc-400">Loading...</div>;
  const stats = [
    { label: "Total Users", value: data.total_users, color: "#074572" },
    { label: "Active", value: data.active_users, color: "#10B981" },
    { label: "Suspended", value: data.suspended, color: "#F59E0B" },
    { label: "Banned", value: data.banned, color: "#EF4444" },
    { label: "Posts", value: data.total_posts, color: "#8B5CF6" },
    { label: "Hidden Posts", value: data.hidden_posts, color: "#F59E0B" },
    { label: "Teams", value: data.total_teams, color: "#06B6D4" },
    { label: "Events", value: data.total_events, color: "#EC4899" },
    { label: "Connections", value: data.total_connections, color: "#074572" },
    { label: "Pending Reports", value: data.pending_reports, color: "#F85037" },
    { label: "Pending Verifications", value: data.pending_verifications, color: "#F59E0B" },
  ];
  return (
    <div data-testid="admin-analytics">
      <h2 className="text-lg font-medium text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Platform Analytics</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {stats.map(s => (
          <div key={s.label} className="bg-white border border-[#E6E6E6] rounded-lg p-4">
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: s.color, fontFamily: 'Outfit' }}>{s.value}</p>
          </div>
        ))}
      </div>
      <h3 className="text-sm font-semibold text-zinc-900 mb-2" style={{ fontFamily: 'Outfit' }}>Roles Distribution</h3>
      <div className="flex flex-wrap gap-2 mb-6">
        {data.roles && Object.entries(data.roles).map(([role, count]) => (
          <Badge key={role} variant="secondary" className="capitalize text-xs">{role}: {count}</Badge>
        ))}
      </div>
    </div>
  );
}

/* ---- Users Section ---- */
function UsersSection() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState({ open: false, action: "", userId: "", title: "", desc: "", destructive: false, requireReason: true });
  const [reason, setReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set("search", search);
      if (roleFilter) params.set("role", roleFilter);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch {}
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const doAction = async () => {
    setActionLoading(true);
    try {
      if (modal.action === "suspend") await api.put(`/admin/users/${modal.userId}/suspend`, { reason, days: 7 });
      else if (modal.action === "ban") await api.put(`/admin/users/${modal.userId}/ban`, { reason });
      else if (modal.action === "unban") await api.put(`/admin/users/${modal.userId}/unban`);
      else if (modal.action === "verify") await api.put(`/admin/users/${modal.userId}/verify`, { type: "athlete" });
      toast.success("Action completed");
      setModal({ ...modal, open: false });
      setReason("");
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  const statusBadge = (s) => {
    if (s === "banned") return <Badge className="bg-red-50 text-red-700 text-[10px]">Banned</Badge>;
    if (s === "suspended") return <Badge className="bg-amber-50 text-amber-700 text-[10px]">Suspended</Badge>;
    return <Badge className="bg-green-50 text-green-700 text-[10px]">Active</Badge>;
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search users..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 border-[#E6E6E6] h-9" data-testid="admin-user-search" />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-36 border-[#E6E6E6] h-9" data-testid="admin-role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {["athlete","scout","coach","club","moderator","admin","super_admin","verification_admin"].map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-36 border-[#E6E6E6] h-9"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-zinc-400 mb-3">{total} users found</p>
      <div className="space-y-2">
        {users.map(u => (
          <div key={u._id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3" data-testid={`admin-user-${u._id}`}>
            <div className="w-9 h-9 rounded-full bg-[#074572] flex items-center justify-center text-white text-xs font-bold shrink-0">
              {u.name?.split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-zinc-900 truncate">{u.name}</p>
                <Badge variant="secondary" className="text-[10px] capitalize">{u.role}</Badge>
                {statusBadge(u.user_status)}
                {u.ergo_verified && <ShieldCheck size={12} className="text-[#074572]" />}
              </div>
              <p className="text-xs text-zinc-400 truncate">{u.email}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              {u.user_status !== "suspended" && u.user_status !== "banned" && (
                <Button size="sm" variant="ghost" className="text-amber-600 text-xs h-7 px-2" onClick={() => setModal({ open: true, action: "suspend", userId: u._id, title: "Suspend User", desc: `Suspend ${u.name} for 7 days?`, destructive: true, requireReason: true })} data-testid={`suspend-${u._id}`}>
                  <AlertTriangle size={12} />
                </Button>
              )}
              {u.user_status !== "banned" && (
                <Button size="sm" variant="ghost" className="text-red-600 text-xs h-7 px-2" onClick={() => setModal({ open: true, action: "ban", userId: u._id, title: "Ban User", desc: `Permanently ban ${u.name}?`, destructive: true, requireReason: true })} data-testid={`ban-${u._id}`}>
                  <Ban size={12} />
                </Button>
              )}
              {(u.user_status === "banned" || u.user_status === "suspended") && (
                <Button size="sm" variant="ghost" className="text-green-600 text-xs h-7 px-2" onClick={() => { setModal({ open: true, action: "unban", userId: u._id, title: "Unban User", desc: `Restore ${u.name}'s access?`, destructive: false, requireReason: false }); }} data-testid={`unban-${u._id}`}>
                  <RotateCcw size={12} />
                </Button>
              )}
              {!u.ergo_verified && u.role === "athlete" && (
                <Button size="sm" variant="ghost" className="text-[#074572] text-xs h-7 px-2" onClick={() => setModal({ open: true, action: "verify", userId: u._id, title: "Verify Athlete", desc: `Verify ${u.name}'s profile?`, destructive: false, requireReason: false })} data-testid={`verify-${u._id}`}>
                  <UserCheck size={12} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-2 mt-4">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#E6E6E6]"><ChevronLeft size={14} /></Button>
        <span className="text-xs text-zinc-500">Page {page}</span>
        <Button variant="outline" size="sm" disabled={users.length < 15} onClick={() => setPage(p => p + 1)} className="border-[#E6E6E6]"><ChevronRight size={14} /></Button>
      </div>
      <ConfirmModal open={modal.open} onClose={(v) => setModal({...modal, open: v})} title={modal.title} description={modal.desc} onConfirm={doAction} loading={actionLoading} destructive={modal.destructive} requireReason={modal.requireReason} reasonValue={reason} onReasonChange={setReason} />
    </div>
  );
}

/* ---- Posts Section ---- */
function PostsSection() {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, action: "", postId: "", title: "", desc: "" });
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set("search", search);
      if (statusF) params.set("status", statusF);
      const { data } = await api.get(`/admin/posts?${params}`);
      setPosts(data.posts);
    } catch {}
  }, [page, search, statusF]);

  useEffect(() => { fetch(); }, [fetch]);

  const doAction = async () => {
    setLoading(true);
    try {
      if (modal.action === "hide") await api.put(`/admin/posts/${modal.postId}/hide`, { reason });
      else if (modal.action === "restore") await api.put(`/admin/posts/${modal.postId}/restore`);
      else if (modal.action === "delete") await api.delete(`/admin/posts/${modal.postId}`, { data: { reason } });
      toast.success("Done");
      setModal({ ...modal, open: false });
      setReason("");
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const statusColor = (s) => {
    if (s === "hidden") return "bg-amber-50 text-amber-700";
    if (s === "deleted") return "bg-red-50 text-red-700";
    return "bg-green-50 text-green-700";
  };

  return (
    <div data-testid="admin-posts">
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input placeholder="Search posts..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 border-[#E6E6E6] h-9" data-testid="admin-post-search" /></div>
        <Select value={statusF} onValueChange={v => { setStatusF(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-36 border-[#E6E6E6] h-9"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="hidden">Hidden</SelectItem><SelectItem value="deleted">Deleted</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {posts.map(p => (
          <div key={p.id} className="bg-white border border-[#E6E6E6] rounded-md p-3" data-testid={`admin-post-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-zinc-900">{p.author_name}</p>
                  <Badge className={`text-[10px] capitalize ${statusColor(p.status || "active")}`}>{p.status || "active"}</Badge>
                  <span className="text-[10px] text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-zinc-600 line-clamp-2">{p.content}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {(p.status === "active" || !p.status) && (
                  <Button size="sm" variant="ghost" className="text-amber-600 h-7 px-2" onClick={() => setModal({ open: true, action: "hide", postId: p.id, title: "Hide Post", desc: "This post will be hidden from the feed." })}><EyeOff size={12} /></Button>
                )}
                {p.status === "hidden" && (
                  <Button size="sm" variant="ghost" className="text-green-600 h-7 px-2" onClick={() => setModal({ open: true, action: "restore", postId: p.id, title: "Restore Post", desc: "Restore this post to the feed?" })}><Eye size={12} /></Button>
                )}
                {p.status !== "deleted" && (
                  <Button size="sm" variant="ghost" className="text-red-600 h-7 px-2" onClick={() => setModal({ open: true, action: "delete", postId: p.id, title: "Delete Post", desc: "Permanently delete this post?" })}><Trash2 size={12} /></Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <ConfirmModal open={modal.open} onClose={v => setModal({...modal, open: v})} title={modal.title} description={modal.desc} onConfirm={doAction} loading={loading} destructive={modal.action === "delete"} requireReason={modal.action !== "restore"} reasonValue={reason} onReasonChange={setReason} />
    </div>
  );
}

/* ---- Reports Section ---- */
function ReportsSection() {
  const [reports, setReports] = useState([]);
  const [statusF, setStatusF] = useState("pending");

  const fetch = useCallback(async () => {
    try { const { data } = await api.get(`/admin/reports?status=${statusF}`); setReports(data); } catch {}
  }, [statusF]);
  useEffect(() => { fetch(); }, [fetch]);

  const resolve = async (id, res) => {
    try { await api.put(`/admin/reports/${id}/resolve`, { resolution: res }); toast.success("Report resolved"); fetch(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex gap-3 mb-4">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40 border-[#E6E6E6] h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="dismissed">Dismissed</SelectItem></SelectContent>
        </Select>
      </div>
      {reports.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No {statusF} reports</p> : (
        <div className="space-y-2">
          {reports.map(r => (
            <div key={r.id} className="bg-white border border-[#E6E6E6] rounded-md p-3" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="text-[10px] capitalize">{r.content_type}</Badge>
                    <span className="text-xs text-zinc-400">by {r.reporter_name}</span>
                  </div>
                  <p className="text-sm text-zinc-700">{r.reason}</p>
                  {r.description && <p className="text-xs text-zinc-500 mt-1">{r.description}</p>}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-1">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" onClick={() => resolve(r.id, "resolved")} data-testid={`resolve-${r.id}`}>Resolve</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7 border-[#E6E6E6]" onClick={() => resolve(r.id, "dismissed")}>Dismiss</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Verifications Section ---- */
function VerificationsSection() {
  const [users, setUsers] = useState([]);
  const fetch = async () => { try { const { data } = await api.get("/admin/verification-requests"); setUsers(data); } catch {} };
  useEffect(() => { fetch(); }, []);

  const action = async (userId, type) => {
    try {
      if (type === "approve") await api.put(`/admin/verification-requests/${userId}/approve`);
      else await api.put(`/admin/verification-requests/${userId}/reject`, { reason: "Does not meet requirements" });
      toast.success(type === "approve" ? "Verified!" : "Rejected");
      fetch();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-verifications">
      {users.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No pending verification requests</p> : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u._id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3" data-testid={`verification-${u._id}`}>
              <div className="flex-1">
                <p className="text-sm font-medium text-zinc-900">{u.name}</p>
                <p className="text-xs text-zinc-400">{u.email} - {u.role} - {u.position}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" onClick={() => action(u._id, "approve")} data-testid={`approve-${u._id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="text-xs h-7 border-[#E6E6E6] text-red-600" onClick={() => action(u._id, "reject")} data-testid={`reject-${u._id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Certifications Section ---- */
function CertificationsSection() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [certs, setCerts] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, certId: null });
  const [rejectReason, setRejectReason] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/certifications?status=${filter}`);
      setCerts(data || []);
    } catch { toast.error("Failed to load certifications"); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const approve = async (id) => {
    try {
      await api.put(`/admin/certifications/${id}/approve`);
      toast.success("Certification approved");
      fetch();
    } catch { toast.error("Failed to approve"); }
  };

  const openReject = (id) => { setRejectReason(""); setRejectModal({ open: true, certId: id }); };

  const confirmReject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.put(`/admin/certifications/${rejectModal.certId}/reject`, { reason: rejectReason });
      toast.success("Certification rejected");
      setRejectModal({ open: false, certId: null });
      setRejectReason("");
      fetch();
    } catch { toast.error("Failed to reject"); }
  };

  const docUrl = (path) => `${BACKEND_URL}/api/files/${path}`;

  return (
    <div data-testid="admin-certifications">
      <div className="flex items-center gap-2 mb-4">
        <Label className="text-xs text-zinc-600">Status:</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 h-8 border-[#E6E6E6] text-xs" data-testid="cert-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-center py-12 text-zinc-400 text-sm">Loading...</p> : certs.length === 0 ? (
        <p className="text-center py-12 text-zinc-400 text-sm">No {filter} certification requests</p>
      ) : (
        <div className="space-y-3">
          {certs.map(c => (
            <div key={c.id} className="bg-white border border-[#E6E6E6] rounded-lg p-4" data-testid={`cert-row-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#074572]/5 flex items-center justify-center shrink-0">
                  <GraduationCap size={18} className="text-[#074572]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-zinc-900">{c.user_name || "User"}</p>
                    <Badge className="text-[10px] bg-[#074572]/5 text-[#074572]">{c.cert_name || "Certification"}</Badge>
                    {c.cert_level && <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{c.cert_level}</Badge>}
                    <Badge className={`text-[10px] ${c.status === "approved" ? "bg-green-50 text-green-700" : c.status === "rejected" ? "bg-red-50 text-red-700" : "bg-zinc-50 text-zinc-600"}`}>
                      {c.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Submitted {new Date(c.created_at).toLocaleString()}</p>
                  {c.original_filename && (
                    <a href={docUrl(c.document_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#074572] hover:underline" data-testid={`cert-doc-link-${c.id}`}>
                      <FileCheck size={12} /> {c.original_filename}
                      <Download size={11} />
                    </a>
                  )}
                  {c.reject_reason && <p className="text-xs text-red-600 mt-2">Rejected: {c.reject_reason}</p>}
                </div>
                {c.status === "pending" && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" onClick={() => approve(c.id)} data-testid={`cert-approve-${c.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7 border-[#E6E6E6] text-red-600" onClick={() => openReject(c.id)} data-testid={`cert-reject-${c.id}`}>Reject</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={rejectModal.open}
        onClose={() => setRejectModal({ open: false, certId: null })}
        title="Reject Certification"
        description="Provide a reason that will be sent to the user."
        onConfirm={confirmReject}
        destructive
        requireReason
        reasonValue={rejectReason}
        onReasonChange={setRejectReason}
      />
    </div>
  );
}

/* ---- Premium / Subscriptions Section ---- */
function PremiumSection() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeModal, setRevokeModal] = useState({ open: false, userId: null, name: "" });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const { data } = await api.get(`/admin/premium-users?${params}`);
      setUsers(data || []);
    } catch { toast.error("Failed to load premium users"); }
    setLoading(false);
  }, [search, sourceFilter]);

  const fetchTxns = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payment-transactions?limit=100");
      setTxns(data || []);
    } catch {}
  }, []);

  useEffect(() => { if (tab === "users") fetchUsers(); else fetchTxns(); }, [tab, fetchUsers, fetchTxns]);

  const handleRevoke = async (reason) => {
    try {
      await api.delete(`/admin/users/${revokeModal.userId}/revoke-premium`, { data: { reason } });
      toast.success("Premium revoked");
      setRevokeModal({ open: false, userId: null, name: "" });
      fetchUsers();
    } catch { toast.error("Failed to revoke"); }
  };

  const stats = {
    total: users.length,
    manual: users.filter(u => u.premium_source === "manual").length,
    stripe: users.filter(u => u.premium_source === "stripe").length,
  };

  return (
    <div data-testid="admin-premium">
      <div className="flex items-center gap-1 mb-6 border-b border-[#E6E6E6]">
        <button onClick={() => setTab("users")} className={`px-3 pb-2.5 text-sm border-b-2 -mb-px ${tab === "users" ? "border-[#074572] text-[#074572] font-medium" : "border-transparent text-zinc-500"}`} data-testid="premium-tab-users">
          Active Subscribers
        </button>
        <button onClick={() => setTab("transactions")} className={`px-3 pb-2.5 text-sm border-b-2 -mb-px ${tab === "transactions" ? "border-[#074572] text-[#074572] font-medium" : "border-transparent text-zinc-500"}`} data-testid="premium-tab-transactions">
          Stripe Transactions
        </button>
      </div>

      {tab === "users" ? (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white border border-[#E6E6E6] rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Total Active</p>
              <p className="text-xl font-semibold text-[#074572] mt-0.5" style={{ fontFamily: 'Outfit' }}>{stats.total}</p>
            </div>
            <div className="bg-white border border-[#E6E6E6] rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Manual Grants</p>
              <p className="text-xl font-semibold text-[#F85037] mt-0.5" style={{ fontFamily: 'Outfit' }}>{stats.manual}</p>
            </div>
            <div className="bg-white border border-[#E6E6E6] rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Stripe Paid</p>
              <p className="text-xl font-semibold text-[#074572] mt-0.5" style={{ fontFamily: 'Outfit' }}>{stats.stripe}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-9 h-9 border-[#E6E6E6]" data-testid="premium-search" />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-36 h-9 border-[#E6E6E6] text-xs" data-testid="premium-source-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setGrantOpen(true)} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 h-9" data-testid="open-grant-premium">
              <Plus size={14} /> Grant Premium
            </Button>
          </div>

          {loading ? <p className="text-center py-12 text-zinc-400 text-sm">Loading...</p> : users.length === 0 ? (
            <p className="text-center py-12 text-zinc-400 text-sm">No premium subscribers found</p>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u._id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3" data-testid={`premium-row-${u._id}`}>
                  <Crown size={18} className="text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-zinc-900">{u.name}</p>
                      <Badge className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{u.role}</Badge>
                      <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{u.premium_plan || "premium"}</Badge>
                      <Badge className={`text-[10px] ${u.premium_source === "manual" ? "bg-purple-50 text-purple-700" : u.premium_source === "stripe" ? "bg-blue-50 text-blue-700" : "bg-zinc-50 text-zinc-600"}`}>
                        {u.premium_source || "legacy"}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{u.email}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {u.premium_granted_at && `Granted ${new Date(u.premium_granted_at).toLocaleDateString()}`}
                      {u.premium_granted_by_name && ` by ${u.premium_granted_by_name}`}
                      {u.premium_expires_at && ` · Expires ${new Date(u.premium_expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="text-xs h-7 border-[#E6E6E6] text-red-600" onClick={() => setRevokeModal({ open: true, userId: u._id, name: u.name })} data-testid={`revoke-${u._id}`}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div data-testid="premium-transactions">
          {txns.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No payment transactions yet</p> : (
            <div className="space-y-2">
              {txns.map((t, idx) => (
                <div key={idx} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                    <Crown size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-zinc-900">{t.user_email || t.user_id}</p>
                      <Badge className={`text-[10px] ${t.payment_status === "paid" ? "bg-green-50 text-green-700" : t.payment_status === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                        {t.payment_status || "unknown"}
                      </Badge>
                      <Badge className="text-[10px] bg-[#074572]/5 text-[#074572]">{t.package_id}</Badge>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {t.amount && `${(t.amount / 100).toFixed(2)} ${(t.currency || "usd").toUpperCase()}`}
                      {t.created_at && ` · ${new Date(t.created_at).toLocaleString()}`}
                    </p>
                    {t.session_id && <p className="text-[10px] text-zinc-400 mt-0.5 truncate">Session: {t.session_id}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <GrantPremiumModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onGranted={() => { setGrantOpen(false); fetchUsers(); }}
      />
      <RevokePremiumModal
        open={revokeModal.open}
        userName={revokeModal.name}
        onClose={() => setRevokeModal({ open: false, userId: null, name: "" })}
        onConfirm={handleRevoke}
      />
    </div>
  );
}

function GrantPremiumModal({ open, onClose, onGranted }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [plan, setPlan] = useState("scout_pro");
  const [expiresAt, setExpiresAt] = useState("");
  const [searching, setSearching] = useState(false);
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); setSelected(null); setPlan("scout_pro"); setExpiresAt(""); }
  }, [open]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/users-search?q=${encodeURIComponent(query)}`);
        if (active) setResults(data || []);
      } catch {}
      finally { if (active) setSearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [query]);

  const handleGrant = async () => {
    if (!selected) { toast.error("Select a user first"); return; }
    setGranting(true);
    try {
      await api.post(`/admin/users/${selected._id}/grant-premium`, { plan, expires_at: expiresAt });
      toast.success(`Premium granted to ${selected.name}`);
      onGranted();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to grant"); }
    finally { setGranting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Crown size={18} className="text-amber-500" /> Grant Premium Access
          </DialogTitle>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-2">
            <Label className="text-xs text-zinc-600">Search user by name or email</Label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type at least 2 characters..." className="border-[#E6E6E6]" data-testid="grant-user-search" autoFocus />
            <div className="max-h-56 overflow-y-auto border border-[#E6E6E6] rounded-md">
              {searching ? <p className="text-center py-4 text-xs text-zinc-400">Searching...</p>
              : results.length === 0 ? <p className="text-center py-4 text-xs text-zinc-400">{query.length < 2 ? "Type to search" : "No users found"}</p>
              : results.map(u => (
                <button key={u._id} type="button" onClick={() => setSelected(u)} className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 flex items-center gap-2" data-testid={`grant-search-result-${u._id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#074572] text-white text-[10px] font-medium flex items-center justify-center">{u.name?.split(" ").map(n => n[0]).join("").toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-900 truncate">{u.name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{u.email} · <span className="capitalize">{u.role}</span></p>
                  </div>
                  {u.is_premium && <Crown size={12} className="text-amber-500" />}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2.5 bg-[#074572]/5 rounded-md">
              <div className="w-9 h-9 rounded-full bg-[#074572] text-white text-xs font-medium flex items-center justify-center">{selected.name?.split(" ").map(n => n[0]).join("").toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900">{selected.name}</p>
                <p className="text-[11px] text-zinc-500">{selected.email} · <span className="capitalize">{selected.role}</span></p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="text-zinc-400 hover:text-[#F85037]"><X size={14} /></button>
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="mt-1 border-[#E6E6E6]" data-testid="grant-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scout_pro">Scout Pro</SelectItem>
                  <SelectItem value="team_pro">Team Pro</SelectItem>
                  <SelectItem value="manual">Manual / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Expires At (optional)</Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-1 border-[#E6E6E6]" data-testid="grant-expires-input" />
              <div className="flex items-center gap-1.5 mt-2">
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Quick trial:</span>
                {[
                  { label: "7 days", days: 7 },
                  { label: "14 days", days: 14 },
                  { label: "30 days", days: 30 },
                ].map(p => (
                  <button
                    key={p.days}
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() + p.days);
                      setExpiresAt(d.toISOString().slice(0, 10));
                    }}
                    className="px-2 py-0.5 text-[11px] font-medium rounded-full border border-[#074572]/20 text-[#074572] hover:bg-[#074572]/5"
                    data-testid={`trial-preset-${p.days}`}
                  >
                    {p.label}
                  </button>
                ))}
                {expiresAt && (
                  <button type="button" onClick={() => setExpiresAt("")} className="px-2 py-0.5 text-[11px] text-zinc-400 hover:text-[#F85037]" data-testid="trial-clear">
                    clear
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Leave empty for unlimited access</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="border-[#E6E6E6]" data-testid="grant-cancel">Cancel</Button>
          <Button onClick={handleGrant} disabled={!selected || granting} className="bg-[#074572] hover:bg-[#053a60] text-white" data-testid="grant-confirm">
            {granting ? "Granting..." : "Grant Premium"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RevokePremiumModal({ open, userName, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <AlertTriangle size={18} className="text-[#F85037]" /> Revoke Premium
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-500">Remove premium access from <span className="font-semibold text-zinc-800">{userName}</span>? This action is audit-logged.</p>
        <div>
          <Label className="text-xs text-zinc-600">Reason (optional)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Trial ended, payment refunded..." className="mt-1 border-[#E6E6E6] min-h-[60px]" data-testid="revoke-reason" />
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="border-[#E6E6E6]" data-testid="revoke-cancel">Cancel</Button>
          <Button onClick={() => onConfirm(reason)} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="revoke-confirm">Revoke Premium</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---- Banned Section ---- */
function BannedSection() {
  const [users, setUsers] = useState([]);
  const fetch = async () => { try { const { data } = await api.get("/admin/banned-users"); setUsers(data); } catch {} };
  useEffect(() => { fetch(); }, []);

  const unban = async (userId) => {
    try { await api.put(`/admin/users/${userId}/unban`); toast.success("Unbanned"); fetch(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-banned">
      {users.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No banned or suspended users</p> : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u._id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-zinc-900">{u.name} <Badge className={`text-[10px] ml-1 ${u.user_status === "banned" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{u.user_status}</Badge></p>
                <p className="text-xs text-zinc-500">{u.ban_reason || u.suspension_reason}</p>
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => unban(u._id)}>Unban</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Ads Section ---- */
const AD_ROLE_OPTIONS = ["athlete", "coach", "scout", "club", "parent"];

function AdsSection() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    headline: "", description: "", target_url: "", sponsor_name: "",
    image_url: "", start_date: "", end_date: "", status: "active",
    target_roles: [], target_locations: [],
  });
  const [locationInput, setLocationInput] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ads");
      setAds(data || []);
    } catch { toast.error("Failed to load ads"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const resetForm = () => {
    setForm({ headline: "", description: "", target_url: "", sponsor_name: "", image_url: "", start_date: "", end_date: "", status: "active", target_roles: [], target_locations: [] });
    setLocationInput("");
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setOpenForm(true);
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setForm({
      headline: ad.headline || "",
      description: ad.description || "",
      target_url: ad.target_url || "",
      sponsor_name: ad.sponsor_name || "",
      image_url: ad.image_url || "",
      start_date: ad.start_date || "",
      end_date: ad.end_date || "",
      status: ad.status || "active",
      target_roles: ad.target_roles || [],
      target_locations: ad.target_locations || [],
    });
    setLocationInput("");
    setImageFile(null);
    setImagePreview(null);
    setOpenForm(true);
  };

  const toggleRole = (role) => {
    setForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role],
    }));
  };

  const addLocation = () => {
    const val = locationInput.trim();
    if (!val) return;
    if (form.target_locations.includes(val)) { setLocationInput(""); return; }
    setForm(prev => ({ ...prev, target_locations: [...prev.target_locations, val] }));
    setLocationInput("");
  };

  const removeLocation = (loc) => {
    setForm(prev => ({ ...prev, target_locations: prev.target_locations.filter(l => l !== loc) }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5MB"); return; }
    setImageFile(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.headline.trim() || !form.description.trim() || !form.target_url.trim() || !form.sponsor_name.trim()) {
      toast.error("Please fill headline, description, target URL and sponsor name");
      return;
    }
    let imageUrl = form.image_url;
    setSaving(true);
    try {
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const { data } = await api.post("/ads/image-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imageUrl = data.image_url;
      }
      if (!imageUrl && !editing) {
        toast.error("Please upload a banner image");
        setSaving(false);
        return;
      }
      const payload = { ...form, image_url: imageUrl };
      if (editing) {
        await api.patch(`/ads/${editing.id}`, payload);
        toast.success("Ad updated");
      } else {
        await api.post("/ads", payload);
        toast.success("Ad created");
      }
      setOpenForm(false);
      resetForm();
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save ad");
    } finally { setSaving(false); }
  };

  const toggleStatus = async (ad) => {
    try {
      const nextStatus = ad.status === "active" ? "paused" : "active";
      await api.patch(`/ads/${ad.id}`, { status: nextStatus });
      toast.success(`Ad ${nextStatus}`);
      fetchAds();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.headline}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/ads/${ad.id}`);
      toast.success("Ad deleted");
      fetchAds();
    } catch { toast.error("Failed to delete"); }
  };

  const copySponsorLink = async (ad) => {
    const url = `${window.location.origin}/sponsor/${ad.id}?token=${encodeURIComponent(ad.share_token || "")}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Sponsor link copied to clipboard");
    } catch {
      // Fallback: prompt with the URL
      window.prompt("Copy this sponsor link:", url);
    }
  };

  const totals = ads.reduce(
    (acc, a) => ({
      impressions: acc.impressions + (a.impressions || 0),
      clicks: acc.clicks + (a.clicks || 0),
      active: acc.active + (a.status === "active" ? 1 : 0),
    }),
    { impressions: 0, clicks: 0, active: 0 }
  );
  const overallCtr = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : "0.00";

  return (
    <div data-testid="admin-ads">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Megaphone size={14} className="text-[#074572]" />
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Total Ads</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }} data-testid="ads-total">{ads.length}</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">{totals.active} active</p>
        </div>
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Eye size={14} className="text-[#F85037]" />
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Impressions</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }} data-testid="ads-impressions">{totals.impressions.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <MousePointerClick size={14} className="text-emerald-600" />
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Clicks</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }} data-testid="ads-clicks">{totals.clicks.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-amber-500" />
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">CTR</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }} data-testid="ads-ctr">{overallCtr}%</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-zinc-500">Sponsored placements shown in the Feed right rail.</p>
        <Button onClick={openCreate} className="bg-[#F85037] hover:bg-[#e04530] text-white gap-1.5" data-testid="ad-create-btn">
          <Plus size={14} /> New Ad
        </Button>
      </div>

      {/* Ads table */}
      {loading ? (
        <div className="p-12 text-center text-zinc-400 text-sm">Loading ads...</div>
      ) : ads.length === 0 ? (
        <div className="p-12 text-center text-zinc-400 text-sm border border-dashed border-[#E6E6E6] rounded-lg">
          No ads yet. Click "New Ad" to create your first sponsored banner.
        </div>
      ) : (
        <div className="border border-[#E6E6E6] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[#E6E6E6]">
              <tr className="text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                <th className="px-4 py-3">Banner</th>
                <th className="px-4 py-3">Ad</th>
                <th className="px-4 py-3">Sponsor</th>
                <th className="px-4 py-3">Targeting</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Impressions</th>
                <th className="px-4 py-3 text-right">Clicks</th>
                <th className="px-4 py-3 text-right">CTR</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6E6E6]">
              {ads.map(ad => {
                const imgSrc = ad.image_url?.startsWith("http") ? ad.image_url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${ad.image_url}`;
                return (
                  <tr key={ad.id} className="hover:bg-zinc-50" data-testid={`ad-row-${ad.id}`}>
                    <td className="px-4 py-3">
                      {ad.image_url ? (
                        <img src={imgSrc} alt={ad.headline} className="w-20 h-11 object-cover rounded-md border border-[#E6E6E6]" />
                      ) : (
                        <div className="w-20 h-11 bg-zinc-100 rounded-md" />
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-medium text-zinc-900 truncate">{ad.headline}</p>
                      <p className="text-xs text-zinc-500 truncate">{ad.description}</p>
                      <a href={ad.target_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#074572] hover:underline inline-flex items-center gap-0.5 mt-0.5">
                        {ad.target_url} <ExternalLink size={10} />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 text-xs">{ad.sponsor_name}</td>
                    <td className="px-4 py-3">
                      {(!ad.target_roles?.length && !ad.target_locations?.length) ? (
                        <span className="text-[10px] text-zinc-400">All users</span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {ad.target_roles?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {ad.target_roles.map(r => (
                                <span key={r} className="text-[9px] px-1.5 py-0.5 bg-[#074572]/5 text-[#074572] rounded capitalize">{r}</span>
                              ))}
                            </div>
                          )}
                          {ad.target_locations?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {ad.target_locations.map(l => (
                                <span key={l} className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded flex items-center gap-0.5">
                                  <MapPin size={8} />{l}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-[10px] capitalize ${ad.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                        {ad.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-700 tabular-nums" data-testid={`ad-imp-${ad.id}`}>{(ad.impressions || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-zinc-700 tabular-nums" data-testid={`ad-clk-${ad.id}`}>{(ad.clicks || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-zinc-700 tabular-nums" data-testid={`ad-ctr-${ad.id}`}>{(ad.ctr || 0).toFixed(2)}%</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => copySponsorLink(ad)} className="h-7 w-7 p-0 text-[#074572]" title="Copy sponsor share link" data-testid={`ad-share-${ad.id}`}>
                          <Link2 size={13} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleStatus(ad)} className="h-7 w-7 p-0" title={ad.status === "active" ? "Pause" : "Activate"} data-testid={`ad-toggle-${ad.id}`}>
                          {ad.status === "active" ? <Pause size={13} /> : <Play size={13} />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(ad)} className="h-7 px-2 text-xs text-[#074572]" data-testid={`ad-edit-${ad.id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(ad)} className="h-7 w-7 p-0 text-[#F85037]" title="Delete" data-testid={`ad-delete-${ad.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={openForm} onOpenChange={(o) => { if (!o) { setOpenForm(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editing ? "Edit Ad" : "New Sponsored Ad"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Banner Image {editing && <span className="text-zinc-400">(leave empty to keep current)</span>}</Label>
              {(imagePreview || form.image_url) && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={imagePreview || (form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${form.image_url}`)}
                    alt="preview"
                    className="max-h-32 rounded-md border border-[#E6E6E6]"
                  />
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageSelect} className="mt-2 text-xs" data-testid="ad-image-input" />
            </div>
            <div>
              <Label className="text-xs">Sponsor Name</Label>
              <Input value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} placeholder="e.g. Nike, adidas, Local Football Club" className="mt-1" data-testid="ad-sponsor-input" />
            </div>
            <div>
              <Label className="text-xs">Headline</Label>
              <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Catchy headline (max 60 chars)" maxLength={60} className="mt-1" data-testid="ad-headline-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short pitch shown under the headline" maxLength={140} className="mt-1 min-h-[60px]" data-testid="ad-description-input" />
            </div>
            <div>
              <Label className="text-xs">Target URL</Label>
              <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://sponsor-site.com" className="mt-1" data-testid="ad-url-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Date (optional)</Label>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="ad-start-input" />
              </div>
              <div>
                <Label className="text-xs">End Date (optional)</Label>
                <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="ad-end-input" />
              </div>
            </div>

            {/* Targeting */}
            <div className="border border-[#E6E6E6] rounded-md p-3 space-y-2 bg-zinc-50/50">
              <div className="flex items-center gap-2">
                <Target size={13} className="text-[#074572]" />
                <p className="text-xs font-semibold text-zinc-700">Audience Targeting</p>
                <span className="text-[10px] text-zinc-400">(leave empty to show to everyone)</span>
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Roles</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ad-roles-container">
                  {AD_ROLE_OPTIONS.map(role => {
                    const on = form.target_roles.includes(role);
                    return (
                      <button
                        type="button"
                        key={role}
                        onClick={() => toggleRole(role)}
                        className={`text-[11px] px-2.5 py-1 rounded-full capitalize border transition-colors ${
                          on ? "bg-[#074572] text-white border-[#074572]" : "bg-white text-zinc-600 border-[#E6E6E6] hover:border-[#074572]/40"
                        }`}
                        data-testid={`ad-role-${role}`}
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Locations (city, country or region — substring match)</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }}
                    placeholder="e.g. London, UK or Barcelona"
                    className="text-xs flex-1"
                    data-testid="ad-location-input"
                  />
                  <Button type="button" size="sm" onClick={addLocation} variant="outline" className="shrink-0" data-testid="ad-location-add">
                    Add
                  </Button>
                </div>
                {form.target_locations.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid="ad-locations-list">
                    {form.target_locations.map(loc => (
                      <span key={loc} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
                        <MapPin size={9} />{loc}
                        <button type="button" onClick={() => removeLocation(loc)} className="ml-0.5 hover:text-red-600">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="ad-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setOpenForm(false); resetForm(); }} data-testid="ad-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="ad-save-btn">
                {saving ? "Saving..." : (editing ? "Update" : "Create Ad")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Parental Links Section ---- */
function ParentalLinksSection() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [childId, setChildId] = useState("");
  const [parentId, setParentId] = useState("");
  const [childOptions, setChildOptions] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/parental/links"); setLinks(data || []); }
    catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!proposeOpen) return;
    // Load candidate athletes 10-15 and parents
    api.get("/scout/discover?role=athlete&limit=200").then(r => {
      const kids = (r.data.users || []).filter(u => u.age && u.age >= 10 && u.age <= 15);
      setChildOptions(kids);
    }).catch(() => {});
    api.get("/scout/discover?role=parent&limit=200").catch(() => ({ data: { users: [] } })).then(r => setParentOptions(r.data.users || []));
  }, [proposeOpen]);

  const propose = async () => {
    if (!childId || !parentId) { toast.error("Choose both a child and a parent"); return; }
    try {
      await api.post("/admin/parental/propose", { child_id: childId, parent_id: parentId });
      toast.success("Proposal created — both parties must accept");
      setProposeOpen(false); setChildId(""); setParentId("");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const unlink = async (id) => {
    if (!window.confirm("Force-unlink this pair? Messaging for the child will lock until they relink.")) return;
    try { await api.delete(`/admin/parental/${id}`); toast.success("Unlinked"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-parental">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">Manage parent ↔ child athlete links across the platform.</p>
        <Button onClick={() => setProposeOpen(true)} className="bg-[#F85037] hover:bg-[#e04530] text-white gap-1.5" data-testid="parental-propose-btn">
          <Plus size={14} /> Propose new link
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-400">Loading...</div>
      ) : links.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-[#E6E6E6] rounded-lg text-sm text-zinc-400">No parental links yet.</div>
      ) : (
        <div className="border border-[#E6E6E6] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[#E6E6E6]">
              <tr className="text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                <th className="px-4 py-3">Child</th>
                <th className="px-4 py-3">Parent</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Initiated</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6E6E6]">
              {links.map(l => (
                <tr key={l.id} className="hover:bg-zinc-50" data-testid={`parental-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{l.child?.name || "?"}</p>
                    <p className="text-[11px] text-zinc-500">{l.child?.email} · age {l.child?.age}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{l.parent?.name || "?"}</p>
                    <p className="text-[11px] text-zinc-500">{l.parent?.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`text-[10px] capitalize ${l.status === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {l.status}
                    </Badge>
                    {l.admin_initiated && (
                      <div className="text-[9px] text-zinc-400 mt-1">
                        Parent ack: {l.parent_ack ? "✓" : "—"} · Child ack: {l.child_ack ? "✓" : "—"}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {l.admin_initiated ? "Admin" : l.initiator_id === l.child_id ? "Child" : "Parent"}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-zinc-500">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => unlink(l.id)} className="h-7 px-2 text-xs text-[#F85037]" data-testid={`parental-admin-unlink-${l.id}`}>
                      <Trash2 size={12} className="mr-1" /> Unlink
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={proposeOpen} onOpenChange={setProposeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Propose Parental Link</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 -mt-2">Both the child and parent must accept from their own account before the link becomes active.</p>
          <div>
            <Label className="text-xs">Child athlete (10–15)</Label>
            <Select value={childId} onValueChange={setChildId}>
              <SelectTrigger className="mt-1" data-testid="parental-propose-child"><SelectValue placeholder="Choose child..." /></SelectTrigger>
              <SelectContent>
                {childOptions.map(c => <SelectItem key={c._id} value={c._id}>{c.name} · age {c.age} · {c.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Parent</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger className="mt-1" data-testid="parental-propose-parent"><SelectValue placeholder="Choose parent..." /></SelectTrigger>
              <SelectContent>
                {parentOptions.map(p => <SelectItem key={p._id} value={p._id}>{p.name} · {p.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#E6E6E6] pt-3">
            <Button variant="outline" onClick={() => setProposeOpen(false)} className="border-[#E6E6E6] h-8 text-xs">Cancel</Button>
            <Button onClick={propose} className="bg-[#F85037] hover:bg-[#e04530] text-white h-8 text-xs" data-testid="parental-propose-submit">Propose</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---- Media Section ---- */
function MediaSection() {
  const [files, setFiles] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/media"); setFiles(data.files || []); } catch {} })(); }, []);
  return (
    <div data-testid="admin-media">
      {files.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No media uploads</p> : (
        <div className="space-y-2">
          {files.map(f => (
            <div key={f.id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3">
              <Image size={16} className="text-zinc-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-900 truncate">{f.original_filename}</p>
                <p className="text-xs text-zinc-400">{f.content_type} - {(f.size / 1024).toFixed(1)} KB - {new Date(f.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Audit Log Section ---- */
function AuditSection() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (actionFilter) params.set("action", actionFilter);
      const { data } = await api.get(`/admin/audit-log?${params}`);
      setLogs(data.logs);
    } catch {}
  }, [page, actionFilter]);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div data-testid="admin-audit">
      <div className="flex gap-3 mb-4">
        <Input placeholder="Filter by action..." value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="border-[#E6E6E6] h-9 max-w-xs" data-testid="audit-filter" />
      </div>
      {logs.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">No audit logs yet</p> : (
        <div className="space-y-1">
          {logs.map(l => (
            <div key={l.id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3 text-xs">
              <Badge variant="secondary" className="text-[10px] capitalize shrink-0">{l.action_type.replace(/_/g, " ")}</Badge>
              <div className="flex-1 min-w-0">
                {l.reason && <span className="text-zinc-500">Reason: {l.reason}</span>}
              </div>
              <span className="text-zinc-400 shrink-0">{new Date(l.created_at).toLocaleString()}</span>
              {l.ip && <span className="text-zinc-300 shrink-0">{l.ip}</span>}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-center gap-2 mt-4">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#E6E6E6]"><ChevronLeft size={14} /></Button>
        <span className="text-xs text-zinc-500">Page {page}</span>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} className="border-[#E6E6E6]"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
}

/* ---- Email Config Section ---- */
function EmailConfigSection() {
  const [health, setHealth] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/health/email"); setHealth(data); } catch {} })();
  }, []);

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-test-email", { to_email: testEmail });
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-email-config">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Email Service Status</h3>
      {health && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className={`p-4 border rounded-lg ${health.brevo_api_key_configured ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <p className="text-[10px] font-semibold uppercase text-zinc-500">BREVO_API_KEY</p>
            <p className={`text-sm font-bold mt-1 ${health.brevo_api_key_configured ? "text-green-700" : "text-red-700"}`}>
              {health.brevo_api_key_configured ? "Configured" : "Not Set"}
            </p>
          </div>
          <div className={`p-4 border rounded-lg ${health.app_base_url_configured ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <p className="text-[10px] font-semibold uppercase text-zinc-500">APP_BASE_URL</p>
            <p className={`text-sm font-bold mt-1 ${health.app_base_url_configured ? "text-green-700" : "text-red-700"}`}>
              {health.app_base_url || "Not Set"}
            </p>
          </div>
          <div className="p-4 border border-[#E6E6E6] rounded-lg col-span-2">
            <p className="text-[10px] font-semibold uppercase text-zinc-500">From Address</p>
            <p className="text-sm font-medium text-zinc-900 mt-1">{health.from_email || "Not configured"}</p>
          </div>
        </div>
      )}
      <Separator className="bg-[#E6E6E6] mb-6" />
      <h3 className="text-sm font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Outfit' }}>Send Test Email</h3>
      <p className="text-xs text-zinc-500 mb-3">Verify your Brevo integration is working by sending a test email.</p>
      <div className="flex gap-2">
        <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="recipient@example.com" className="border-[#E6E6E6] flex-1" data-testid="test-email-input" />
        <Button onClick={sendTest} disabled={sending} className="bg-[#074572] hover:bg-[#053a60] text-white gap-2 shrink-0" data-testid="send-test-email-btn">
          <Mail size={14} /> {sending ? "Sending..." : "Send Test"}
        </Button>
      </div>
      <div className="mt-6 p-4 bg-[#074572]/5 border border-[#074572]/10 rounded-lg">
        <h4 className="text-xs font-semibold text-[#074572] mb-2">Email Flows Active</h4>
        <ul className="text-xs text-zinc-600 space-y-1">
          <li>Email verification on signup</li>
          <li>Password reset requests</li>
          <li>Welcome email on registration</li>
          <li>Admin alerts for critical actions</li>
          <li>Content report notifications to admin</li>
          <li>Connection request notifications</li>
        </ul>
      </div>
    </div>
  );
}

/* ---- Platform Settings Section ---- */
function PlatformSettingsSection() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/admin/platform-settings"); setSettings(data); } catch {} })();
  }, []);

  const togglePro = async () => {
    setSaving(true);
    try {
      const newVal = !settings?.pro_enabled;
      const { data } = await api.put("/admin/platform-settings", { pro_enabled: newVal });
      setSettings(data);
      toast.success(newVal ? "Pro subscriptions enabled" : "Pro subscriptions hidden from users");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-center py-12 text-zinc-400">Loading...</div>;

  return (
    <div data-testid="admin-platform-settings">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Feature Toggles</h3>
      <p className="text-xs text-zinc-500 mb-6">Control which features are visible to users on the platform.</p>

      <div className="space-y-4">
        {/* Pro Toggle */}
        <div className={`p-5 border-2 rounded-lg transition-colors ${settings.pro_enabled ? "bg-green-50 border-green-200" : "bg-zinc-50 border-[#E6E6E6]"}`} data-testid="pro-toggle-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.pro_enabled ? "bg-green-100" : "bg-zinc-100"}`}>
                {settings.pro_enabled ? <ToggleRight size={22} className="text-green-600" /> : <ToggleLeft size={22} className="text-zinc-400" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-900">Pro Subscriptions</p>
                <p className="text-xs text-zinc-500 mt-0.5">Show or hide the "Pro" button and Premium page for all users</p>
              </div>
            </div>
            <Button
              onClick={togglePro}
              disabled={saving}
              className={`${settings.pro_enabled ? "bg-green-600 hover:bg-green-700" : "bg-zinc-400 hover:bg-zinc-500"} text-white min-w-[100px]`}
              data-testid="pro-toggle-btn"
            >
              {saving ? "Saving..." : settings.pro_enabled ? "Enabled" : "Disabled"}
            </Button>
          </div>
          <div className={`mt-3 p-3 rounded-md text-xs ${settings.pro_enabled ? "bg-green-100/50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
            {settings.pro_enabled
              ? "Pro button is visible in the navbar and user dropdown. Users can access the Premium page and initiate Stripe checkout."
              : 'Pro button, Premium page, and all subscription CTAs are hidden from users. The "/premium" route will still exist but won\'t be promoted.'}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Cleanup Section ---- */
function CleanupSection() {
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/admin/cleanup-test-data/preview"); setPreview(data); } catch {} })();
  }, []);

  const runCleanup = async () => {
    if (confirmation !== "DELETE TEST DATA") { toast.error("Confirmation phrase must be: DELETE TEST DATA"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data", { confirmation });
      setResult(data.results);
      toast.success(`Cleanup complete! ${data.results.total_deleted} records removed.`);
      setShowConfirm(false);
      setConfirmation("");
      const { data: updated } = await api.get("/admin/cleanup-test-data/preview");
      setPreview(updated);
    } catch (err) { toast.error(err.response?.data?.detail || "Cleanup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-cleanup">
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-red-600" />
          <p className="text-sm font-semibold text-red-800">Danger Zone</p>
        </div>
        <p className="text-xs text-red-600">This will permanently delete all test/demo data. Super admin access required. This action cannot be undone.</p>
      </div>

      {preview && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Outfit' }}>Test Data Preview</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Test Users", value: preview.test_users },
              { label: "Test Posts", value: preview.test_posts },
              { label: "Test Events", value: preview.test_events },
              { label: "Test Teams", value: preview.test_teams },
              { label: "Test Messages", value: preview.test_messages },
              { label: "Test Connections", value: preview.test_connections },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[#E6E6E6] rounded-md p-3">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase">{s.label}</p>
                <p className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-xs text-green-800 font-medium">Protected (will NOT be deleted):</p>
            <p className="text-xs text-green-600 mt-1">{preview.protected?.join(", ")}</p>
          </div>
        </div>
      )}

      {result && (
        <div className="mb-6 p-4 bg-[#074572]/5 border border-[#074572]/10 rounded-lg">
          <h3 className="text-sm font-semibold text-[#074572] mb-2" style={{ fontFamily: 'Outfit' }}>Cleanup Results</h3>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {Object.entries(result).filter(([k]) => k !== "total_deleted").map(([k, v]) => (
              <div key={k}><span className="text-zinc-500">{k.replace(/_/g, " ")}:</span> <span className="font-bold text-zinc-900">{v}</span></div>
            ))}
          </div>
          <p className="text-sm font-bold text-[#074572] mt-2">Total: {result.total_deleted} records deleted</p>
        </div>
      )}

      {!showConfirm ? (
        <Button className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" onClick={() => setShowConfirm(true)} data-testid="cleanup-start-btn">
          <Trash2 size={14} /> Start Test Data Cleanup
        </Button>
      ) : (
        <div className="p-4 bg-white border-2 border-red-300 rounded-lg">
          <p className="text-sm text-zinc-900 font-medium mb-3">Type <code className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-bold">DELETE TEST DATA</code> to confirm:</p>
          <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} placeholder="Type confirmation phrase..." className="border-red-200 mb-3" data-testid="cleanup-confirmation-input" />
          <div className="flex gap-2">
            <Button onClick={runCleanup} disabled={loading || confirmation !== "DELETE TEST DATA"} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="cleanup-confirm-btn">
              {loading ? "Cleaning up..." : "Confirm Deletion"}
            </Button>
            <Button variant="outline" onClick={() => { setShowConfirm(false); setConfirmation(""); }} className="border-[#E6E6E6]" data-testid="cleanup-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Main Admin Dashboard ---- */
export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        await api.get("/admin/check");
        setAuthorized(true);
        const { data } = await api.get("/admin/analytics");
        setAnalytics(data);
      } catch {
        setAuthorized(false);
      }
      setChecking(false);
    };
    check();
  }, []);

  if (checking) return <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-[#074572] border-t-transparent rounded-full animate-spin" /></div>;
  if (!authorized) return (
    <div className="flex items-center justify-center h-96 text-center">
      <div>
        <Lock size={48} className="mx-auto text-zinc-300 mb-4" />
        <h2 className="text-xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Access Denied</h2>
        <p className="text-sm text-zinc-500 mt-1">You don't have admin privileges to access this page.</p>
        <Button className="mt-4 bg-[#074572] text-white" onClick={() => navigate("/feed")}>Go to Feed</Button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "analytics": return <AnalyticsSection data={analytics} />;
      case "users": return <UsersSection />;
      case "posts": return <PostsSection />;
      case "reports": return <ReportsSection />;
      case "verifications": return <VerificationsSection />;
      case "certifications": return <CertificationsSection />;
      case "premium": return <PremiumSection />;
      case "ads": return <AdsSection />;
      case "parental": return <ParentalLinksSection />;
      case "banned": return <BannedSection />;
      case "media": return <MediaSection />;
      case "audit": return <AuditSection />;
      case "email": return <EmailConfigSection />;
      case "settings": return <PlatformSettingsSection />;
      case "cleanup": return <CleanupSection />;
      default: return <AnalyticsSection data={analytics} />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#074572] flex items-center justify-center">
          <Shield size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Admin Dashboard</h1>
          <p className="text-xs text-zinc-500">Logged in as <span className="font-medium capitalize">{user?.role?.replace(/_/g, " ")}</span></p>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="hidden md:block w-52 shrink-0">
          <nav className="bg-white border border-[#E6E6E6] rounded-lg p-2 sticky top-20 space-y-0.5" data-testid="admin-sidebar">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const active = activeTab === item.id;
              return (
                <button key={item.id} onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ${
                    active ? "bg-[#074572] text-white" : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                  data-testid={`admin-nav-${item.id}`}>
                  <Icon size={16} /> {item.label}
                  {item.id === "reports" && analytics?.pending_reports > 0 && (
                    <span className="ml-auto w-5 h-5 bg-[#F85037] text-white text-[10px] rounded-full flex items-center justify-center">{analytics.pending_reports}</span>
                  )}
                  {item.id === "certifications" && analytics?.pending_certifications > 0 && (
                    <span className="ml-auto w-5 h-5 bg-[#F85037] text-white text-[10px] rounded-full flex items-center justify-center" data-testid="cert-pending-badge">{analytics.pending_certifications}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white border border-[#E6E6E6] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              {(() => { const item = sidebarItems.find(i => i.id === activeTab); const Icon = item?.icon || BarChart3; return <Icon size={18} className="text-[#074572]" />; })()}
              <h2 className="text-lg font-medium text-zinc-900 capitalize" style={{ fontFamily: 'Outfit' }}>
                {sidebarItems.find(i => i.id === activeTab)?.label || "Analytics"}
              </h2>
            </div>
            <Separator className="bg-[#E6E6E6] mb-4" />
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
